import React, { Component } from "react";
import PropTypes from "prop-types";
import queryString from "query-string-for-all";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import {
  IconButton,
  Magnifier as SearchIcon,
  Remove,
  color,
  spacing,
  borderRadius,
  palette,
  typography,
} from "@conserveit/blocks";
import { CONTROLS } from "../../../components/AppChrome/App.constants";

const StyledSearch = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 0 ${spacing.snug};
  border: 1px solid ${palette.neutral5};
  border-radius: ${borderRadius.default};
  overflow: hidden;
  background: ${color.pureWhite};

  input {
    display: block;
    height: ${CONTROLS.HEIGHTS.BASE};
    width: 100%;
    padding: 0 ${spacing.snug};
    border: none;
    appearance: default;
    background: transparent;
    ${typography.body}
  }

  /* Hide native search clear icon in webkit */
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  input:focus {
    outline: none;
    box-shadow: none;
  }
`;

// TODO: This is be unnecessary once search is in Blocks
const Clear = styled(IconButton)`
  svg {
    height: 16px;
    width: 16px;
  }
`;

class Search extends Component {
  state = {
    // eslint-disable-next-line
    lastQuery: "",
    query: "",
  };

  componentDidMount() {
    const { urlSearchKey, location } = this.props;

    // If a search param key is provided, set initial search of that parameter's value
    if (urlSearchKey) {
      const query = queryString.parse(location.search)[urlSearchKey];
      this.setQuery(query, false);
    }
  }

  /**
   * @param {string} query Search query to be used
   * @param {bool} updateParams Flag to say whether to also update url search param
   */
  setQuery = (query = "", updateParams = true) => {
    this.setState(
      {
        // eslint-disable-next-line
        lastQuery: query,
        query,
      },
      () => {
        this.props.onChange({ target: { value: query } });
        if (this.props.urlSearchKey && updateParams) {
          this.updateQueryString(query);
        }
      }
    );
  };

  /**
   * @param {string} query String to apply to url search parameter
   *
   * This method updates the url search parameter that matches [urlSearchParameter] from props
   */
  updateQueryString = (query) => {
    const { history, urlSearchKey } = this.props;
    const qs = queryString.parse(history.location.search);

    if (
      qs[urlSearchKey] !== undefined &&
      qs[urlSearchKey] === this.state.search
    )
      return;

    const activeSearch = query.length > 0 ? query : undefined;
    const stringified = queryString.stringify({
      ...qs,
      [urlSearchKey]: activeSearch,
    });
    history.replace({
      search: stringified,
    });
  };

  resetQuery = () => {
    this.setState(
      (state) => ({
        query: state.lastQuery,
      }),
      () => {
        this.props.inputRef.current.select();
      }
    );
  };

  handleChange = (event) => {
    this.setState({
      query: event.target.value,
    });
  };

  handleSearch = (event) => {
    if (event.key === "Enter") {
      this.setQuery(event.target.value);
    }

    if (event.key === "Escape") {
      this.resetQuery();
    }
  };

  clearSearch = () => {
    this.setQuery("");
  };

  handleUpdate = () => {
    const { query } = this.state;
    this.setQuery(query);
  };

  showUpdate = () => {
    const { onEnter } = this.props;
    const { query, lastQuery } = this.state;
    return !onEnter ? false : query.trim() !== lastQuery.trim();
  };

  render() {
    const {
      inputRef,
      isEmpty,
      noClear,
      onChange,
      onEnter,
      placeholder,
      className,
    } = this.props;

    const { query } = this.state;
    return (
      <StyledSearch className={className}>
        {onEnter ? (
          <input
            type="input"
            onKeyDown={this.handleSearch}
            value={query}
            onChange={this.handleChange}
            ref={inputRef}
            placeholder={placeholder}
            data-cy="Insights-searchInput"
          />
        ) : (
          <input
            type="search"
            onChange={onChange}
            ref={inputRef}
            placeholder={placeholder}
          />
        )}
        {onEnter && !noClear && !isEmpty && !this.showUpdate() && (
          <Clear
            className="t-ClearSearch"
            data-cy="Insights-clearSearchInput"
            onClick={this.clearSearch}
            small
            icon={<Remove />}
          />
        )}
        {onEnter && (this.showUpdate() || isEmpty) && (
          <IconButton
            data-cy="Insights-confirmSearch"
            onClick={this.handleUpdate}
            disabled={!this.state.query && !this.state.lastQuery}
            small
            icon={<SearchIcon />}
          />
        )}
      </StyledSearch>
    );
  }
}

Search.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  noClear: PropTypes.bool,
  onChange: PropTypes.func,
  onEnter: PropTypes.bool,
  placeholder: PropTypes.string,
  initialSearch: PropTypes.string,
  urlSearchKey: PropTypes.string,
};

Search.defaultProps = {
  children: null,
  className: "",
  inputRef: null,
  noClear: false,
  onChange: () => {},
  onEnter: false,
  placeholder: "",
  initialSearch: "",
  history: { location: {} },
  urlSearchKey: undefined,
};

export default withRouter(Search);
